import {
  ChangeDetectionStrategy,
  Component,
  signal,
  computed,
  input,
  inject,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { DetailConnectedItemService, Layer, LayerLogsResponse } from '../../services/detail-connected-item.service';
import { LAYER_NAMES, STATUS_LABELS } from '../../models/conexoes.model';
import { Subject, take, takeUntil } from 'rxjs';
import { SkeletonDirective } from 'src/app/shared/directives/skeleton/skeleton.directive';
import { PipelineStageCardComponent } from '../pipeline-stage-card/pipeline-stage-card.component';

type LogLine = {
  message: string;
  status: 'failure' | 'completed' | 'processing';
}

const LAYER_SKELETON_PLACEHOLDERS = ['Bronze', 'Prata', 'Ouro'] as const;

@Component({
  selector: 'app-pipeline-layers-console',
  standalone: true,
  imports: [CommonModule, MatIconModule, PipelineStageCardComponent, SkeletonDirective],
  templateUrl: './pipeline-layers-console.component.html',
  styleUrls: ['./pipeline-layers-console.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PipelineLayersConsoleComponent {
  private readonly detailService = inject(DetailConnectedItemService);
  private readonly destroy$ = new Subject<void>();

  private readonly mapLayerNames = LAYER_NAMES;
  private readonly mapLayerStatus = STATUS_LABELS;
  readonly layerSkeletonPlaceholders = LAYER_SKELETON_PLACEHOLDERS;

  readonly layers = input<Layer[]>([]);
  readonly isLoading = input(false);
  /** Quando definido (ex.: vindo de "Acompanhar logs"), abre o console dessa camada. */
  readonly initialSelectedLayerId = input<string | null>(null);
  readonly logs = signal<LogLine[]>([]);
  readonly jobId = input<string | null>(null);
  readonly selectedLayerId = signal<string | null>(null);
  private hasConsumedInitial = signal(false);

  /** Camadas exibidas nos cards; atualizado pelo input e por layer:updated do socket (steps, status). */
  private readonly layersDisplay = signal<Layer[]>([]);

  readonly isConsoleOpen = computed(() => this.selectedLayerId() !== null);
  readonly layersList = computed(() => this.layersDisplay());

  constructor() {
    this.detailService.getLayerLogsUpdates().pipe(
      takeUntil(this.destroy$)
    ).subscribe((log) => {
      this.prepareNewLogLine(log);
    });

    this.detailService.getLayerUpdates().pipe(
      takeUntil(this.destroy$)
    ).subscribe((updated) => {
      if (updated.job_id !== this.jobId()) return;
      this.layersDisplay.update((prev) => {
        const idx = prev.findIndex((l) => l.layer === updated.layer);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          ...updated,
          // Alguns payloads de socket não trazem _id; preserva o anterior para tracking estável.
          _id: updated._id || next[idx]._id,
        };
        return next;
      });
    });

    effect(() => {
      const inputLayers = this.layers();
      this.layersDisplay.set([...inputLayers]);
    });

    effect(() => {
      const initialId = this.initialSelectedLayerId();
      if (!initialId) {
        this.hasConsumedInitial.set(false);
        return;
      }
      if (this.hasConsumedInitial()) return;
      const list = this.layersList();
      const layer = list.find((l) => l._id === initialId);
      if (layer) {
        this.hasConsumedInitial.set(true);
        this.openConsole(layer);
        this.selectedLayerId.set(layer._id);
      }
    });
  }

  openConsole(layer: Layer): void {
    if(!layer._id) return;
    this.selectedLayerId.set(layer._id);
    this.getLogs(layer._id);
  }

  private readonly getLogs = (layerId: string): void => {
    this.detailService.getLayerLogs(layerId).pipe(
      take(1)
    ).subscribe((logs) => {
      this.prepareLogs(logs);
    });
  }

  closeConsole(): void {
    this.selectedLayerId.set(null);
  }

  prepareLogs(logs: LayerLogsResponse[]): void {
    const logsLines: LogLine[] = logs.map((log) => ({
      message: this.formatLogLine(log),
      status: log.status as 'failure' | 'completed' | 'processing'
    } as LogLine));
    this.logs.set(logsLines);
  }

  private readonly formatDate = (date: string): string => {
    return new Date(date).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  private readonly formatLogLine = (log: LayerLogsResponse): string => {
    const formattedDate = this.formatDate(log.time);
    return `${formattedDate} ${log.output}`;
  }

  prepareNewLogLine(log: LayerLogsResponse): void {
    const isSameLayer = log.layer_id === this.selectedLayerId();
    if (!isSameLayer) return;
    if (!log.output || log.output === '') return;
    const newLogLine: LogLine = {
      message: this.formatLogLine(log),
      status: log.status as 'failure' | 'completed' | 'processing'
    } as LogLine;
    const newLogs = [...this.logs(), newLogLine];
    this.logs.set(newLogs);
  }
}
